import React, { useMemo } from "react"
import {
  FormControl, FormLabel, VStack,
} from "@chakra-ui/react"
import {
  ExportFormat, SheetLayout, SHEET_FORMAT_OPTIONS, getSheetForFormat,
  dimensionsMatch, calculateSheetGrid, FitStatus,
} from "components/BadgeBuilder/utils/sheetPresets"
import { BadgeTemplate } from "sharedTypes/badge"
import { DioboxEvent } from "sharedTypes"
import { GuestData } from "components/BadgeBuilder/utils/badgeDataResolver"
import SheetFormatCard from "./SheetFormatCard"

type Props = {
  selectedFormat: ExportFormat
  onFormatChange: (format: ExportFormat) => void
  customSheet: SheetLayout | null
  onCustomSheetConfigure: () => void
  selectedBadge: BadgeTemplate | null
  previewGuests: GuestData[]
  event?: DioboxEvent
  selectedCount: number
  startPosition: number
}

type FormatInfo = {
  sheet: SheetLayout | null
  grid: { columns: number; rows: number } | null
  fitStatus: FitStatus | null
}

const SheetFormatSelector = ({
  selectedFormat, onFormatChange, customSheet, onCustomSheetConfigure,
  selectedBadge, previewGuests, event, selectedCount, startPosition,
}: Props) => {
  const handleCardClick = (format: ExportFormat) => {
    onFormatChange(format)
    if (format === ExportFormat.CUSTOM_SHEET && !customSheet) {
      onCustomSheetConfigure()
    }
  }

  const formatInfoMap = useMemo(() => new Map<ExportFormat, FormatInfo>(
    SHEET_FORMAT_OPTIONS.map((o) => {
      const sheet = o.format === ExportFormat.CUSTOM_SHEET
        ? customSheet : getSheetForFormat(o.format)
      const grid = sheet ? calculateSheetGrid(sheet) : null

      let fitStatus: FitStatus | null = null
      if (o.format !== ExportFormat.SINGLE && selectedBadge && sheet) {
        const isMatch = dimensionsMatch(
          selectedBadge.widthMm, selectedBadge.heightMm,
          sheet.labelWidthMm, sheet.labelHeightMm,
        )
        fitStatus = isMatch ? "perfect" : "scales"
      }

      return [o.format, { sheet, grid, fitStatus }]
    }),
  ), [selectedBadge, customSheet])

  return (
    <FormControl>
      <FormLabel>Sheet Format</FormLabel>
      <VStack spacing={2} align="stretch">
        {SHEET_FORMAT_OPTIONS.map((option) => {
          const isSelected = selectedFormat === option.format
          const info = formatInfoMap.get(option.format)!

          return (
            <SheetFormatCard
              key={option.format}
              option={option}
              isSelected={isSelected}
              onClick={() => handleCardClick(option.format)}
              sheet={info.sheet}
              grid={info.grid}
              fitStatus={info.fitStatus}
              selectedBadge={selectedBadge}
              previewGuests={previewGuests}
              event={event}
              selectedCount={selectedCount}
              startPosition={startPosition}
              onEditLayout={option.format
                === ExportFormat.CUSTOM_SHEET ? onCustomSheetConfigure : undefined}
            />
          )
        })}
      </VStack>
    </FormControl>
  )
}

export default SheetFormatSelector
